import React, { useState, useEffect } from 'react';
import PolymerLineChart, { LineDataset } from '@/components/charts/PolymerLineChart';
import { CheckboxDropdown } from '@/components/common/CheckboxDropdown';
import { useTheme } from '@/contexts/ThemeContext';

const COMMODITIES = [
  { key: 'spot_ethylene', label: 'Spot Ethylene' },
  { key: 'ethane', label: 'Ethane' },
  { key: 'propane', label: 'Propane' },
  { key: 'spot_rgp', label: 'Spot RGP' },
  { key: 'spot_pgp', label: 'Spot PGP' },
  { key: 'spot_benzene', label: 'Spot Benzene' },
  { key: 'spot_styrene', label: 'Spot Styrene' },
  { key: 'spot_butadiene', label: 'Spot Butadiene' },
  { key: 'wti_crude_oil', label: 'WTI Crude' },
  { key: 'brent_crude_oil', label: 'Brent Crude' },
  { key: 'nat_gas', label: 'Nat. Gas' },
];

interface PetchemRunningDailyProps {
  petchemData: any[];
}

const PetchemRunningDaily: React.FC<PetchemRunningDailyProps> = ({ petchemData }) => {
  const { isDark } = useTheme();
  const [selected, setSelected] = useState('spot_ethylene');
  const [isSmallRunningDaily, setIsSmallRunningDaily] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 750px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 750px)');
    setIsSmallRunningDaily(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsSmallRunningDaily(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const data = petchemData;
  const labels = data.map((d) => String(d.date).slice(0, 10));
  const values = data.map((d) => {
    const v = d[selected];
    return v !== null && v !== undefined ? Number(v) : null;
  });

  const selectedLabel = COMMODITIES.find((c) => c.key === selected)?.label || selected;
  const datasets: LineDataset[] = [{ data: values, label: selectedLabel }];

  return (
    <div className={`flex flex-col gap-4 ${isSmallRunningDaily ? 'p-0 -mx-6' : ''}`}>
      <div className={`flex items-center gap-3 ${isSmallRunningDaily ? 'justify-center px-4' : ''}`}>
        <CheckboxDropdown
          label="Commodity"
          options={COMMODITIES.map((c) => c.label)}
          selected={new Set([COMMODITIES.find((c) => c.key === selected)?.label || ''])}
          onChange={(selected) => {
            const label = Array.from(selected)[0];
            const commodity = COMMODITIES.find((c) => c.label === label);
            if (commodity) setSelected(commodity.key);
          }}
          singleSelect={true}
          hideCount={true}
          shiftMenuRight={true}
        />
      </div>

      <div className={`border border-rs-jet-light rounded-lg ${isSmallRunningDaily ? 'p-0' : 'p-4'} ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
        <h3 className={`text-rs-off-white font-medium text-sm mb-3 ${isSmallRunningDaily ? 'px-4 pt-4 text-center' : ''}`}>{selectedLabel} — Running Daily</h3>
        {petchemData.length === 0 ? (
          <div className="h-[700px] flex items-center justify-center text-rs-off-white/40 text-sm">No data</div>
        ) : (
          <div className={`h-[700px] ${isSmallRunningDaily ? 'px-4 pb-4' : ''}`}>
            <PolymerLineChart datasets={datasets} labels={labels} compact showRangeSlider className="w-full h-full" />
          </div>
        )}
      </div>
    </div>
  );
};

export default PetchemRunningDaily;
